import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId, useCurrentUser } from "../auth";
import { toast } from "@/hooks/use-toast";
import { VAPID_PUBLIC_KEY, isPushSupported, urlBase64ToUint8Array } from "@/lib/push";

export type PushStatus = "unsupported" | "idle" | "denied" | "subscribed" | "working";

export function usePushSubscription() {
  const { orgId } = useCurrentOrgId();
  const { user } = useCurrentUser();
  const userId = user?.user_id;
  const [status, setStatus] = useState<PushStatus>("idle");

  // Estado inicial: já existe assinatura neste navegador?
  useEffect(() => {
    if (!isPushSupported()) {
      setStatus("unsupported");
      return;
    }
    if (Notification.permission === "denied") {
      setStatus("denied");
      return;
    }
    navigator.serviceWorker
      .getRegistration()
      .then((reg) => reg?.pushManager.getSubscription())
      .then((sub) => setStatus(sub ? "subscribed" : "idle"))
      .catch(() => setStatus("idle"));
  }, []);

  const subscribe = useCallback(async () => {
    if (!isPushSupported()) {
      toast({ title: "Indisponível", description: "Este navegador não suporta notificações push.", variant: "destructive" });
      return;
    }
    if (!userId) return;

    setStatus("working");
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setStatus(permission === "denied" ? "denied" : "idle");
        return;
      }

      const registration = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      const existing = await registration.pushManager.getSubscription();
      const subscription =
        existing ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        }));

      const json = subscription.toJSON();
      const { error } = await supabase.from("push_subscriptions" as any).upsert(
        {
          user_id: userId,
          org_id: orgId ?? null,
          endpoint: json.endpoint,
          p256dh: json.keys?.p256dh,
          auth: json.keys?.auth,
          user_agent: navigator.userAgent,
          last_used_at: new Date().toISOString(),
        } as any,
        { onConflict: "endpoint" }
      );
      if (error) throw error;

      setStatus("subscribed");
      toast({ title: "Push ativado", description: "Você receberá notificações neste dispositivo." });
    } catch (e) {
      console.error("[push] subscribe failed:", e);
      setStatus("idle");
      toast({ title: "Erro", description: "Não foi possível ativar o push.", variant: "destructive" });
    }
  }, [orgId, userId]);

  const unsubscribe = useCallback(async () => {
    setStatus("working");
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await supabase.from("push_subscriptions" as any).delete().eq("endpoint", sub.endpoint);
        await sub.unsubscribe();
      }
      setStatus("idle");
      toast({ title: "Push desativado", description: "Você não receberá mais push neste dispositivo." });
    } catch (e) {
      console.error("[push] unsubscribe failed:", e);
      setStatus("subscribed");
    }
  }, []);

  return {
    status,
    isSupported: status !== "unsupported",
    isSubscribed: status === "subscribed",
    isWorking: status === "working",
    subscribe,
    unsubscribe,
  };
}
